import { ReactNode, Suspense, lazy, useEffect, useLayoutEffect, useState } from 'react';
import {
  createHashRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import { ErrorPage } from '@/pages/ErrorPage';
import { captureOAuthParams, isSsoRedirectPending } from '@asi-lda/pnet-auth-web';
import { ApiProvider } from '@/provider/ApiProvider';
import { Hub } from 'aws-amplify/utils';
import { hasSessionHint } from '@/utils/accessTokenCopy';
import { isSignedIn } from '@/auth/session';
import { RoleGate } from '@/auth/RoleGate';
import { PageGate } from '@/auth/PageGate';
import { SettingsGate } from '@/auth/SettingsGate';
import { CapabilityGate } from '@/pages/access-admin/tenant/CapabilityGate';
import { NotificationsGate } from '@/components/shared/Notifications/NotificationsGate';
import RetiredRedirect from '@/routes/RetiredRedirect';
import { RouteErrorBoundary } from '@/routes/RouteErrorBoundary';
import { SignedOutRedirect } from '@/routes/SignedOutRedirect';
import { usageEnabled } from '@/usage/config';
import { UsageTracker } from '@/usage/useUsage';

type AuthHubPayload = {
  event: string;
  data?: unknown;
};

// Capture OAuth callback params before Amplify or re-renders can clear them.
// Cognito cannot redirect to hash URLs (e.g. /#/sso), so it redirects to the
// plain origin. We detect the params here and forward them into the hash router.
// `captureOAuthParams` reads the query string AND a hash-router query, and
// counts only `code`/`error` as a callback: `state` alone is NOT one - Cognito's
// /logout endpoint redirects back with only ?state=xxx, and treating that as a
// callback trapped the user in a / -> /sso -> Back to Login -> / loop.
const _hasOAuthCallbackParams = captureOAuthParams().hasCallbackParams;

const LoginPage = lazy(() => import('@/pages/login'));
const ResetPasswordPage = lazy(() => import('@/pages/resetpassword'));
const SSOLogin = lazy(() => import('@/pages/sso').then((module) => ({ default: module.SSOLogin })));
const SecurityPage = lazy(() => import('@/pages/security'));

const BrandBonusComponent = lazy(() => import('@/components/BrandProfile/Brandbonus'));
const LoginPreferences = lazy(() => import('@/components/BrandProfile/LoginPreferences'));
const TermsPage = lazy(() => import('@/pages/Termsofuse'));
const AddActivityPage = lazy(() => import('@/pages/addActivity'));
const AddActivityProductLaunchPage = lazy(() => import('@/pages/addActivityProductLaunch'));
const ProductLaunchStepperPage = lazy(() => import('@/pages/productLaunchStepperPage'));
const ViewActivityPage = lazy(() => import('@/pages/viewActivity'));
const AddBrand = lazy(() => import('@/pages/addbrand'));
const AddProperty = lazy(() => import('@/pages/addproperty'));
const AudienceInterestPage = lazy(() => import('@/pages/audience_interest'));
const BrandProfilePage = lazy(() => import('@/pages/brandprofile'));
const ContractFormPage = lazy(() => import('@/pages/contract'));
const LightContractPage = lazy(() => import('@/pages/lightContract'));
const ContractViewPage = lazy(() => import('@/pages/contractview'));
const CurrencyExchangePage = lazy(() => import('@/pages/currencyexchangerate'));
const DashboardPage = lazy(() => import('@/pages/dashboard'));
const DbsPage = lazy(() => import('@/pages/dbs'));
const HeadToHeadPage = lazy(() => import('@/pages/headtohead'));
const ListViewPage = lazy(() => import('@/pages/listview'));
const NoPermissionPage = lazy(() => import('@/pages/nopermission'));
const NoAccessPage = lazy(() => import('@/pages/noaccess'));
const RolePermissionPage = lazy(() => import('@/pages/rolepermission'));
const ReportsPage = lazy(() => import('@/pages/reports'));
const StrategicFitPage = lazy(() => import('@/pages/strategicfit'));
// User administration on the access model (Authorization-Plan §13.9). The old
// editor's paths redirect here.
const AccessUsersPage = lazy(() => import('@/pages/access-admin/UsersListPage'));
const AccessUserDetailPage = lazy(() => import('@/pages/access-admin/UserDetailPage'));
const LegacyUserRedirect = lazy(() => import('@/pages/access-admin/LegacyUserRedirect'));
const MyProfilePage = lazy(() => import('@/pages/myprofile'));
// The notification centre (pnet-docs notifications/Notification-Centre-Design.md): every
// signed-in user, no permission; the not-found page without a notification backend.
const NotificationsPage = lazy(() => import('@/pages/notifications/NotificationsPage'));
const NotificationSettingsPage = lazy(
  () => import('@/pages/notifications/NotificationSettingsPage'),
);
// Settings > Roles in the new admin style (access-admin/RolesListPage); the old
// pages/userrolelist stays in the code but no route renders it any more.
const RolesListPage = lazy(() => import('@/pages/access-admin/RolesListPage'));
// Settings > Manage Users > Rules: the security rules (Authorization-Plan §13.16).
const SecurityRulesPage = lazy(() => import('@/pages/access-admin/SecurityRulesPage'));
const OrganisationsPage = lazy(() => import('@/pages/access-admin/OrganisationsPage'));
const AccessReviewPage = lazy(() => import('@/pages/access-admin/AccessReviewPage'));
// Tenant administration (phase 3 part 1)
const ApprovalsPage = lazy(() => import('@/pages/access-admin/tenant/ApprovalsPage'));
const AccessHistoryPage = lazy(() => import('@/pages/access-admin/tenant/AccessHistoryPage'));
const MembershipsRedirect = lazy(() => import('@/pages/access-admin/tenant/MembershipsRedirect'));
const InvitationPage = lazy(() => import('@/pages/access-admin/tenant/InvitationPage'));
const RequestAccessPage = lazy(() => import('@/pages/access-admin/tenant/RequestAccessPage'));
const WorkInProgressPage = lazy(() => import('@/pages/workinprogress'));
const ThemeEditForm = lazy(() => import('@/components/shared/Forms/ThemeEdit/ThemeEditForm'));
const EmailTemplates = lazy(() => import('@/pages/emailtemplates'));
const AddEmailTemplates = lazy(() => import('@/pages/addemailtemplates'));
const EditEmailTemplate = lazy(() => import('@/pages/editemailtemplate'));
const AddActivation = lazy(() => import('@/pages/addActivation/addActivation'));
const ViewActivation = lazy(() => import('@/pages/ViewActivation'));
const AthleteProfile = lazy(() => import('@/pages/AthleteProfile'));
const ActivationSelectionPage = lazy(() => import('@/pages/comparison/ActivationSelectionPage'));
const PartnerSelectionPage = lazy(() => import('@/pages/comparison/PartnerSelectionPage'));
const ComparisonPage = lazy(() => import('@/pages/comparison/ComparisonPage'));
const CompetitionPage = lazy(() => import('@/pages/comparison/CompetitionPage'));
const RetrospectivePage = lazy(() => import('@/pages/comparison/RetrospectivePage'));
const CreateActivationPage = lazy(() => import('@/pages/comparison/CreateActivationPage'));
const DueDiligenceWizard = lazy(() => import('@/pages/due-diligence/DueDiligenceWizard'));
const DashboardExecutivePage = lazy(() => import('@/pages/dashboard-executive'));
const DashboardDetailedPage = lazy(() => import('@/pages/dashboard-detailed'));
const RunningCalculationsPage = lazy(() => import('@/pages/running-calculations'));
const ExpertsPage = lazy(() => import('@/pages/experts'));
const GovernancePage = lazy(() => import('@/pages/governance'));
const GovernanceReadinessPage = lazy(
  () => import('@/pages/governance/GovernanceReadinessAssessment'),
);
const GovernanceMethodologyPage = lazy(() => import('@/pages/governance/GovernanceMethodology'));
const RightsPage = lazy(() => import('@/pages/rights'));
const BenchmarkGroupsPage = lazy(() => import('@/pages/benchmark-groups'));
const BrandOnboardingPage = lazy(() => import('@/pages/brand-onboarding'));
const PartnerOnboardingPage = lazy(() => import('@/pages/partner-onboarding'));
const DecisionGymPage = lazy(() => import('@/pages/decision-gym'));
const AIPPTCreatorPage = lazy(() => import('@/pages/decision-gym/AIPPTCreator'));
const InvestmentBriefPage = lazy(() => import('@/pages/investment-brief'));
const RouteLoadingFallback = () => <div className='main-loader-area' />;

// Usage analytics (usage/config.ts): off unless the build switches it on. While
// off, nothing of it is mounted and its SDK chunk is never requested.
const USAGE_ANALYTICS_ON = usageEnabled();

/**
 * Signed in on this page: the in-memory session holds the user (set by a
 * sign-in on this page, or by the page-load session check that runs before
 * the app renders, auth/SessionProvider.tsx). Read at render time, like the
 * stored user was before: a sign-in or sign-out in progress does not re-route
 * the page under the flow that runs it.
 */
const isAuthenticated = () => Boolean(isSignedIn() && hasSessionHint());

// Higher-Order Component for Authenticated routes
const AuthenticatedRoute = ({ element }: { element: ReactNode }) => {
  if (isAuthenticated()) {
    return (
      // Reset on every new location (RouteErrorBoundary): one failed page no longer blocks
      // all later navigation.
      <RouteErrorBoundary fallback={ErrorPage}>
        <Suspense fallback={<RouteLoadingFallback />}>{element}</Suspense>
      </RouteErrorBoundary>
    );
  }
  // nobody signed in: the login page, and the sign-in returns to this address
  return <SignedOutRedirect />;
};

// Higher-Order Component for Unauthenticated routes
const UnauthenticatedRoute = ({ element }: { element: ReactNode }) => {
  if (!isAuthenticated()) {
    return <Suspense fallback={<RouteLoadingFallback />}>{element}</Suspense>;
  }
  return <Navigate to='/dashboard' />;
};

// Root layout of every route.
function RootLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const isRootLoginPath = location.pathname === '/';
  const shouldRouteToSso = isRootLoginPath && (_hasOAuthCallbackParams || isSsoRedirectPending());

  // Forward Cognito OAuth callback params into the hash router so SSOLogin
  // can handle them. Runs before browser paint to avoid a flash of login page.
  useLayoutEffect(() => {
    if (shouldRouteToSso && location.pathname !== '/sso') {
      // Keep OAuth params in browser search (window.location.search) so Amplify
      // can complete the code exchange reliably, and only move hash-route to /sso.
      navigate('/sso', { replace: true });
    }
  }, [location.pathname, navigate, shouldRouteToSso]);

  // Hub listener: backup SSO detection when Amplify clears URL params before
  // _oauthCode is captured above (race condition with enable-oauth-listener).
  // signInWithRedirect fires AFTER code exchange completes — SSOLogin's
  // getSessionIdTokenWithRetry() will find the session immediately.
  useEffect(() => {
    const remove = Hub.listen('auth', ({ payload }: { payload: AuthHubPayload }) => {
      if (payload.event === 'signInWithRedirect') {
        navigate('/sso', { replace: true });
      } else if (payload.event === 'signInWithRedirect_failure') {
        const err = (payload.data as any)?.error;
        const desc = err?.message || String(err || 'SSO authentication failed');
        navigate(`/sso?error=oauth_error&error_description=${encodeURIComponent(desc)}`, {
          replace: true,
        });
      }
    });
    return remove;
  }, [navigate]);

  // Validate local session against the backend on route transitions so stale
  // tabs/devices are redirected before expensive page actions start firing.
  useEffect(() => {
    if (location.pathname === '/sso') return;
    if (!isAuthenticated()) return;
    void ApiProvider.ensureSessionAlive(false);
  }, [location.pathname, location.search, location.hash]);

  // Also re-check when the tab/window becomes active again. This covers the
  // "Sign out all devices" case while a page is left open in the background.
  useEffect(() => {
    const probe = () => {
      if (window.location.hash.includes('/sso')) return;
      if (!isAuthenticated()) return;
      void ApiProvider.ensureSessionAlive(true);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        probe();
      }
    };

    window.addEventListener('focus', probe);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('focus', probe);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  if (shouldRouteToSso && location.pathname !== '/sso') {
    return <RouteLoadingFallback />;
  }

  // Signed-in page loads first confirm the active role (auth/RoleGate.tsx).
  return (
    <>
      {USAGE_ANALYTICS_ON && <UsageTracker routes={router.routes} />}
      <RoleGate
        authenticated={isAuthenticated()}
        pathname={location.pathname}
        fallback={<RouteLoadingFallback />}
      >
        <Outlet />
      </RoleGate>
    </>
  );
}

const ContractRoute = () => {
  const [hasPermission, setHasPermission] = useState(false);

  useEffect(() => {
    setHasPermission(ApiProvider.permission('contract_light'));
  }, []);

  return hasPermission ? <LightContractPage /> : <ContractFormPage />;
};

/**
 * Roles > Permissions (`alltype`) or Tiles (any other type): each has its own
 * settings gate (auth/settingsGates.ts).
 */
const RolePermissionRoute = () => {
  const { pagetype } = useParams();
  return (
    <SettingsGate
      gate={pagetype === 'alltype' ? 'rolePermissions' : 'roleTiles'}
      fallback={<RouteLoadingFallback />}
    >
      <RolePermissionPage />
    </SettingsGate>
  );
};

/** In-app links without a page (see the routes below the comparison routes). */
const DEAD_LINKS = [
  '/scouting',
  '/login',
  '/governance/readiness',
  '/governance/methodology',
  '/strategic-relevance/config-choice',
];

const router = createHashRouter(
  createRoutesFromElements(
    <Route element={<RootLayout />}>
      {/* ---------------------- UNAUTHENTICATED ROUTES ---------------------- */}
      <Route
        path='/sso'
        element={
          <Suspense fallback={<RouteLoadingFallback />}>
            <SSOLogin />
          </Suspense>
        }
      />
      <Route
        path='/resetpassword'
        element={<UnauthenticatedRoute element={<ResetPasswordPage />} />}
      />
      <Route path='/' element={<UnauthenticatedRoute element={<LoginPage />} />} />
      {/* Tenant administration: signed in or not, and before the person has any role */}
      <Route
        path='/invitation'
        element={
          <RouteErrorBoundary fallback={ErrorPage}>
            <Suspense fallback={<RouteLoadingFallback />}>
              <InvitationPage />
            </Suspense>
          </RouteErrorBoundary>
        }
      />
      <Route
        path='/request-access'
        element={
          <RouteErrorBoundary fallback={ErrorPage}>
            <Suspense fallback={<RouteLoadingFallback />}>
              <RequestAccessPage />
            </Suspense>
          </RouteErrorBoundary>
        }
      />

      {/* ---------------------- AUTHENTICATED ROUTES ---------------------- */}
      <Route path='/dashboard' element={<AuthenticatedRoute element={<DashboardPage />} />} />
      <Route path='/decision-gym' element={<AuthenticatedRoute element={<DecisionGymPage />} />} />
      <Route
        path='/decision-gym/ai-ppt-creator'
        element={<AuthenticatedRoute element={<AIPPTCreatorPage />} />}
      />
      <Route
        path='/partnership-due-diligence'
        element={<AuthenticatedRoute element={<DueDiligenceWizard />} />}
      />
      <Route
        path='/investment-brief'
        element={<AuthenticatedRoute element={<InvestmentBriefPage />} />}
      />
      <Route
        path='/running-calculations'
        element={<AuthenticatedRoute element={<RunningCalculationsPage />} />}
      />
      <Route
        path='/dashboard-executive'
        element={<AuthenticatedRoute element={<DashboardExecutivePage />} />}
      />
      <Route
        path='/dashboard-detailed'
        element={<AuthenticatedRoute element={<DashboardDetailedPage />} />}
      />
      <Route
        path='/loginpreferences'
        element={
          <AuthenticatedRoute
            element={
              <SettingsGate gate='loginPreferences' fallback={<RouteLoadingFallback />}>
                <LoginPreferences />
              </SettingsGate>
            }
          />
        }
      />
      <Route
        path='/company-sso'
        element={
          <AuthenticatedRoute
            element={
              <SettingsGate gate='companySso' fallback={<RouteLoadingFallback />}>
                <LoginPreferences organisation='internal' />
              </SettingsGate>
            }
          />
        }
      />
      <Route path='/security' element={<AuthenticatedRoute element={<SecurityPage />} />} />
      <Route path='/experts' element={<AuthenticatedRoute element={<ExpertsPage />} />} />
      <Route path='/governance' element={<AuthenticatedRoute element={<GovernancePage />} />} />
      <Route
        path='/governance/readiness'
        element={<AuthenticatedRoute element={<GovernanceReadinessPage />} />}
      />
      <Route
        path='/governance/methodology'
        element={<AuthenticatedRoute element={<GovernanceMethodologyPage />} />}
      />
      <Route path='/rights' element={<AuthenticatedRoute element={<RightsPage />} />} />
      <Route
        path='/benchmark-groups'
        element={<AuthenticatedRoute element={<BenchmarkGroupsPage />} />}
      />
      <Route
        path='/brand-onboarding'
        element={<AuthenticatedRoute element={<BrandOnboardingPage />} />}
      />
      <Route
        path='/partner-onboarding'
        element={<AuthenticatedRoute element={<PartnerOnboardingPage />} />}
      />

      <Route path='/theme/edit' element={<AuthenticatedRoute element={<ThemeEditForm />} />} />

      <Route
        path='/emailtemplates'
        element={
          <AuthenticatedRoute
            element={
              <SettingsGate gate='emailTemplates' fallback={<RouteLoadingFallback />}>
                <EmailTemplates />
              </SettingsGate>
            }
          />
        }
      />
      <Route
        path='/addemailtemplate'
        element={
          <AuthenticatedRoute
            element={
              <SettingsGate gate='emailTemplateCreate' fallback={<RouteLoadingFallback />}>
                <AddEmailTemplates />
              </SettingsGate>
            }
          />
        }
      />
      <Route
        path='/emailtemplates/:id/edit'
        element={
          <AuthenticatedRoute
            element={
              <SettingsGate gate='emailTemplateEdit' fallback={<RouteLoadingFallback />}>
                <EditEmailTemplate />
              </SettingsGate>
            }
          />
        }
      />

      <Route
        path='/brandbonus'
        element={<AuthenticatedRoute element={<BrandBonusComponent />} />}
      />
      <Route
        path='/strategicfit/:id/:email'
        element={<AuthenticatedRoute element={<StrategicFitPage />} />}
      />
      <Route path='/Termsofuse' element={<AuthenticatedRoute element={<TermsPage />} />} />
      <Route
        path='/workinprogress'
        element={<AuthenticatedRoute element={<WorkInProgressPage />} />}
      />
      <Route path='/nopermission' element={<AuthenticatedRoute element={<NoPermissionPage />} />} />
      <Route path='/noaccess' element={<AuthenticatedRoute element={<NoAccessPage />} />} />
      <Route path='/dbs' element={<AuthenticatedRoute element={<DbsPage />} />} />
      <Route
        path='/currencyexchange'
        element={
          <AuthenticatedRoute
            element={
              <SettingsGate gate='currencyExchange' fallback={<RouteLoadingFallback />}>
                <CurrencyExchangePage />
              </SettingsGate>
            }
          />
        }
      />
      <Route
        path='/headtohead'
        element={
          <AuthenticatedRoute
            element={
              <PageGate page='headToHead'>
                <HeadToHeadPage />
              </PageGate>
            }
          />
        }
      />
      {/* Retired (part 7g): the ROI tab of the contract view holds the revenue output. */}
      <Route
        path='/revenueoutput/:id'
        element={<AuthenticatedRoute element={<RetiredRedirect route='revenueOutput' />} />}
      />
      <Route
        path='/audienceinterest'
        element={<AuthenticatedRoute element={<AudienceInterestPage />} />}
      />

      {/* User Management */}
      <Route
        path='/userlist'
        element={
          <AuthenticatedRoute
            element={
              <SettingsGate gate='users' fallback={<RouteLoadingFallback />}>
                <AccessUsersPage />
              </SettingsGate>
            }
          />
        }
      />
      <Route path='/users/new' element={<Navigate to='/userlist' replace />} />
      <Route
        path='/users/:id'
        element={
          <AuthenticatedRoute
            element={
              <SettingsGate gate='users' fallback={<RouteLoadingFallback />}>
                <AccessUserDetailPage />
              </SettingsGate>
            }
          />
        }
      />
      <Route
        path='/adduser'
        element={
          <AuthenticatedRoute
            element={
              <SettingsGate gate='userCreate' fallback={<RouteLoadingFallback />}>
                <LegacyUserRedirect />
              </SettingsGate>
            }
          />
        }
      />
      <Route
        path='/adduser/:type/:id'
        element={
          <AuthenticatedRoute
            element={
              <SettingsGate gate='users' fallback={<RouteLoadingFallback />}>
                <LegacyUserRedirect />
              </SettingsGate>
            }
          />
        }
      />
      <Route path='/myprofile' element={<AuthenticatedRoute element={<MyProfilePage />} />} />
      <Route
        path='/notifications'
        element={
          <AuthenticatedRoute
            element={
              <NotificationsGate fallback={<RouteLoadingFallback />}>
                <NotificationsPage />
              </NotificationsGate>
            }
          />
        }
      />
      <Route
        path='/notification-settings'
        element={
          <AuthenticatedRoute
            element={
              <NotificationsGate fallback={<RouteLoadingFallback />}>
                <NotificationSettingsPage />
              </NotificationsGate>
            }
          />
        }
      />
      <Route
        path='/userrolelist'
        element={
          <AuthenticatedRoute
            element={
              <SettingsGate gate='roles' fallback={<RouteLoadingFallback />}>
                <RolesListPage />
              </SettingsGate>
            }
          />
        }
      />
      <Route
        path='/security-rules'
        element={
          <AuthenticatedRoute
            element={
              <CapabilityGate capability='rules' fallback={<RouteLoadingFallback />}>
                <SecurityRulesPage />
              </CapabilityGate>
            }
          />
        }
      />
      <Route
        path='/organisations'
        element={
          <AuthenticatedRoute
            element={
              <SettingsGate gate='organisations' fallback={<RouteLoadingFallback />}>
                <OrganisationsPage />
              </SettingsGate>
            }
          />
        }
      />
      <Route
        path='/access-review'
        element={
          <AuthenticatedRoute
            element={
              <SettingsGate gate='accessReview' fallback={<RouteLoadingFallback />}>
                <AccessReviewPage />
              </SettingsGate>
            }
          />
        }
      />
      <Route
        path='/settings/access-history'
        element={
          <AuthenticatedRoute
            element={
              <CapabilityGate capability='history' fallback={<RouteLoadingFallback />}>
                <AccessHistoryPage />
              </CapabilityGate>
            }
          />
        }
      />
      {/* the Memberships page folded into All Users (owner 2026-10-16) */}
      <Route
        path='/settings/memberships'
        element={<AuthenticatedRoute element={<MembershipsRedirect />} />}
      />
      <Route
        path='/settings/approvals'
        element={
          <AuthenticatedRoute
            element={
              <CapabilityGate
                capability={['approve', 'my_requests']}
                fallback={<RouteLoadingFallback />}
              >
                <ApprovalsPage />
              </CapabilityGate>
            }
          />
        }
      />
      <Route path='/reports' element={<AuthenticatedRoute element={<ReportsPage />} />} />
      <Route
        path='/rolepermission/:pagetype/:id'
        element={<AuthenticatedRoute element={<RolePermissionRoute />} />}
      />

      {/* Manage Brands, Properties, Campaigns (New: Activations), Activations (New: Activities) and Contracts */}
      <Route
        path='/listview/:pageName'
        element={<AuthenticatedRoute element={<ListViewPage />} />}
      />

      <Route path='/addbrand' element={<AuthenticatedRoute element={<AddBrand />} />} />
      <Route
        path='/brandprofile/:id'
        element={
          <AuthenticatedRoute
            element={
              <PageGate page='brandProfile'>
                <BrandProfilePage />
              </PageGate>
            }
          />
        }
      />
      <Route
        path='/brandprofile/:id/:flag'
        element={
          <AuthenticatedRoute
            element={
              <PageGate page='brandProfile'>
                <BrandProfilePage />
              </PageGate>
            }
          />
        }
      />

      {/* Retired (part 7g): a static mock, unlinked. */}
      <Route
        path='/brand-overview/:id'
        element={<AuthenticatedRoute element={<RetiredRedirect route='brandOverview' />} />}
      />

      <Route path='/addproperty' element={<AuthenticatedRoute element={<AddProperty />} />} />
      <Route
        path='/partnerProfile/:propertyId'
        element={
          <AuthenticatedRoute
            element={
              <PageGate page='partnerProfile'>
                <AthleteProfile />
              </PageGate>
            }
          />
        }
      />
      <Route
        path='/partnerProfile/:propertyId/:brandId'
        element={
          <AuthenticatedRoute
            element={
              <PageGate page='partnerProfile'>
                <AthleteProfile />
              </PageGate>
            }
          />
        }
      />
      {/* Retired (part 7g): the partner profile replaced the property profile. */}
      <Route
        path='/propertyprofile/:id/:flag'
        element={<AuthenticatedRoute element={<RetiredRedirect route='propertyProfile' />} />}
      />
      <Route
        path='/propertyprofile/:id'
        element={<AuthenticatedRoute element={<RetiredRedirect route='propertyProfile' />} />}
      />

      <Route path='/addActivation' element={<AuthenticatedRoute element={<AddActivation />} />} />
      <Route
        path='/viewActivation/:id'
        element={
          <AuthenticatedRoute
            element={
              <PageGate page='campaignView'>
                <ViewActivation />
              </PageGate>
            }
          />
        }
      />

      {/* Retired (part 7g): campaigns are created on /addActivation. */}
      <Route
        path='/storecampaign'
        element={<AuthenticatedRoute element={<RetiredRedirect route='storeCampaign' />} />}
      />
      {/* Retired (part 7j): the campaign view is /viewActivation/:id. */}
      <Route
        path='/storecampaign/:id'
        element={<AuthenticatedRoute element={<RetiredRedirect route='storeCampaignId' />} />}
      />
      {/* Retired (part 7g): the campaign view is /viewActivation/:id. */}
      <Route
        path='/viewcampaign/:id'
        element={<AuthenticatedRoute element={<RetiredRedirect route='viewCampaign' />} />}
      />

      {/* Retired (part 7j): activities are created on /addActivity. */}
      <Route
        path='/activationform/:idtype/:id'
        element={<AuthenticatedRoute element={<RetiredRedirect route='activationForm' />} />}
      />
      <Route
        path='/activationform'
        element={<AuthenticatedRoute element={<RetiredRedirect route='activationForm' />} />}
      />
      <Route path='/addActivity' element={<AuthenticatedRoute element={<AddActivityPage />} />} />
      <Route
        path='/addActivity/:activationId'
        element={<AuthenticatedRoute element={<AddActivityProductLaunchPage />} />}
      />
      <Route
        path='/productLaunch/:activationId'
        element={<AuthenticatedRoute element={<ProductLaunchStepperPage />} />}
      />
      {/* Retired (part 7g): the activity view is /viewActivity/:id. */}
      <Route
        path='/editandviewactivation/:id'
        element={<AuthenticatedRoute element={<RetiredRedirect route='editAndViewActivation' />} />}
      />
      <Route
        path='/viewActivity/:id'
        element={
          <AuthenticatedRoute
            element={
              <PageGate page='activityView'>
                <ViewActivityPage />
              </PageGate>
            }
          />
        }
      />
      <Route
        path='/addcontract/:idType/:id'
        element={<AuthenticatedRoute element={<ContractRoute />} />}
      />
      <Route path='/addcontract' element={<AuthenticatedRoute element={<ContractRoute />} />} />
      <Route
        path='/viewcontract/:id'
        element={
          <AuthenticatedRoute
            element={
              <PageGate page='contractView'>
                <ContractViewPage />
              </PageGate>
            }
          />
        }
      />

      {/* ---------------------- COMPARISON ROUTE ---------------------- */}
      {/* Entry point: Choose Activation */}
      <Route
        path='/comparison'
        element={
          <AuthenticatedRoute
            element={
              <PageGate page='comparison'>
                <ActivationSelectionPage />
              </PageGate>
            }
          />
        }
      />
      {/* Step 2: Select Brands for an activation */}
      <Route
        path='/comparison/activation/:id'
        element={
          <AuthenticatedRoute
            element={
              <PageGate page='comparison'>
                <PartnerSelectionPage />
              </PageGate>
            }
          />
        }
      />
      {/* Direct comparison view (with brands pre-selected via URL params) */}
      <Route
        path='/comparison/view'
        element={
          <AuthenticatedRoute
            element={
              <PageGate page='comparisonView'>
                <ComparisonPage />
              </PageGate>
            }
          />
        }
      />
      <Route
        path='/comparison/competition'
        element={
          <AuthenticatedRoute
            element={
              <PageGate page='comparison'>
                <CompetitionPage />
              </PageGate>
            }
          />
        }
      />
      <Route
        path='/comparison/retrospective'
        element={<AuthenticatedRoute element={<RetrospectivePage />} />}
      />
      <Route
        path='/comparison/create-activation'
        element={<AuthenticatedRoute element={<CreateActivationPage />} />}
      />

      {/* Addresses the app itself links to but no page has (the brand profile's Back without
          a return address, the Terms decline, the Governance buttons, a Decision Gym card).
          They do what the catch-all does (home); declared only so usage analytics names them
          instead of counting them as `/not-found`. */}
      {DEAD_LINKS.map((path) => (
        <Route key={path} path={path} element={<RedirectToHome />} />
      ))}
      <Route path='*' element={<RedirectToHome />} />
    </Route>,
  ),
);

const Index = () => {
  return <RouterProvider router={router} />;
};

function RedirectToHome() {
  const navigate = useNavigate();

  useEffect(() => {
    navigate('/');
  }, [navigate]);

  return null;
}

export default Index;
